import React, { useEffect, useState } from 'react';
import { muxService } from '../../services/muxService';
import { supabase } from '../../services/supabase';
import { useAdminAuth } from '../../context/AdminAuthContext';
import LiveChat from '../LiveChat';
import '../../styles/StreamsManager.css';

const StreamsManager = () => {
  const { user, hasPermission } = useAdminAuth();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createStreamName, setCreateStreamName] = useState('');
  const [creatingStream, setCreatingStream] = useState(false);
  const [selectedStream, setSelectedStream] = useState(null);
  const [showGameModal, setShowGameModal] = useState(false);
  const [showCreateGameModal, setShowCreateGameModal] = useState(false);
  const [archivingStream, setArchivingStream] = useState(null);
  const [weekGames, setWeekGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [teamStandings, setTeamStandings] = useState([]);
  const [selectedGameForStream, setSelectedGameForStream] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [archivedVODs, setArchivedVODs] = useState([]);
  const [loadingVODs, setLoadingVODs] = useState(false);
  const [streamVODs, setStreamVODs] = useState({});

  useEffect(() => {
    if (user) {
      fetchStreams();
      fetchArchivedVODs();
    }
  }, [user]);

  const fetchStreamVOD = async (streamId) => {
    try {
      const { data: vod, error: vodError } = await supabase
        .from('vod_archive')
        .select('id, title, mux_asset_id, playback_id, duration_seconds, created_at')
        .eq('mux_stream_id', streamId)
        .single();

      if (!vodError && vod) {
        setStreamVODs(prev => ({
          ...prev,
          [streamId]: vod
        }));
      }
    } catch (err) {
      console.log('No VOD found for stream:', streamId);
    }
  };

  const fetchStreams = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: userStreams, error: dbError } = await supabase
        .from('streams_metadata')
        .select('mux_stream_id, stream_name, display_title')
        .eq('user_id', user.id);

      if (dbError) throw dbError;

      const userStreamIds = (userStreams || []).map(s => s.mux_stream_id);
      const streamMetadataMap = {};
      (userStreams || []).forEach(s => {
        streamMetadataMap[s.mux_stream_id] = {
          stream_name: s.stream_name,
          display_title: s.display_title
        };
      });

      const allStreams = await muxService.listLiveStreams();
      const filteredStreams = (allStreams.data || [])
        .filter(s => userStreamIds.includes(s.id))
        .map(s => ({
          ...s,
          metadata: streamMetadataMap[s.id] || {}
        }));

      setStreams(filteredStreams);
    } catch (err) {
      console.error('Error fetching streams:', err);
      const errorMsg = err.message || 'Failed to fetch streams. Check browser console for details.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const initiateStreamCreation = async () => {
    if (!createStreamName.trim()) {
      setError('Please enter a stream name');
      return;
    }

    try {
      setLoadingGames(true);
      setError(null);

      const { data: latestWeek, error: weekError } = await supabase
        .from('weeks')
        .select('id, week_number, season_id')
        .order('week_number', { ascending: false })
        .limit(1);

      if (weekError) throw weekError;
      if (!latestWeek || latestWeek.length === 0) {
        setError('No weeks available');
        return;
      }

      const week = latestWeek[0];
      setCurrentWeek(week);
      const seasonId = week.season_id;

      const { data: games, error: gamesError } = await supabase
        .from('schedule')
        .select(`
          id,
          week_id,
          home_team_id,
          away_team_id,
          home_team:teams!schedule_home_team_id_fkey(id, name, logo_url),
          away_team:teams!schedule_away_team_id_fkey(id, name, logo_url)
        `)
        .eq('week_id', week.id)
        .order('id', { ascending: true });

      if (gamesError) throw gamesError;

      const { data: standings, error: standingsError } = await supabase
        .from('team_standings')
        .select(`
          id,
          rank,
          wins,
          losses,
          points_for,
          points_against,
          team:teams(id, name, logo_url)
        `)
        .eq('season_id', seasonId)
        .order('rank', { ascending: true });

      if (standingsError) throw standingsError;

      setWeekGames(games || []);
      setTeamStandings(standings || []);
      setShowCreateGameModal(true);
    } catch (err) {
      console.error('Error loading games:', err);
      setError(err.message);
    } finally {
      setLoadingGames(false);
    }
  };

  const createNewStream = async (game) => {
    if (!game) {
      setError('Please select a game');
      return;
    }

    try {
      setCreatingStream(true);

      const homeStanding = teamStandings.find(s => s.team?.id === game.home_team_id);
      const awayStanding = teamStandings.find(s => s.team?.id === game.away_team_id);

      const streamTitle = `Week ${currentWeek.week_number} ${game.home_team?.name} (${homeStanding?.rank || '-'}) @ ${game.away_team?.name} (${awayStanding?.rank || '-'})`;

      const response = await muxService.createLiveStream(streamTitle);
      let newStream = response.data;

      const fullStreamResponse = await muxService.getLiveStream(newStream.id);
      newStream = fullStreamResponse.data;

      const { error: dbError } = await supabase.from('streams_metadata').insert({
        mux_stream_id: newStream.id,
        user_id: user.id,
        stream_name: createStreamName,
        game_id: game.id,
        display_title: streamTitle,
      });

      if (dbError) throw dbError;

      const streamWithMetadata = {
        ...newStream,
        metadata: {
          stream_name: createStreamName,
          display_title: streamTitle
        }
      };

      setStreams([streamWithMetadata, ...streams]);
      setCreateStreamName('');
      setSelectedGameForStream(null);
      setShowCreateGameModal(false);
      setError(null);
    } catch (err) {
      console.error('Error creating stream:', err);
      setError(err.message);
    } finally {
      setCreatingStream(false);
    }
  };

  const copyToClipboard = (text, event) => {
    if (event) {
      event.stopPropagation();
    }
    navigator.clipboard.writeText(text);
  };

  const disableStream = async (streamId) => {
    try {
      await muxService.disableLiveStream(streamId);
      setStreams(streams.map((s) => (s.id === streamId ? { ...s, status: 'disabled' } : s)));
    } catch (err) {
      console.error('Error disabling stream:', err);
      setError(err.message);
    }
  };

  const archiveStreamVOD = async (stream) => {
    if (!stream.recent_asset_ids || stream.recent_asset_ids.length === 0) {
      setError('No recording available for this stream');
      return;
    }

    try {
      setCreatingStream(true);

      const { data: streamMetadata, error: metaError } = await supabase
        .from('streams_metadata')
        .select('display_title')
        .eq('mux_stream_id', stream.id)
        .single();

      if (metaError) throw metaError;

      const assetId = stream.recent_asset_ids[0];
      const assetResponse = await muxService.getAsset(assetId);
      const asset = assetResponse.data;

      const vodTitle = streamMetadata?.display_title || `Stream - ${new Date().toLocaleDateString()}`;
      const playbackId = asset.playback_ids?.[0]?.id;
      const durationSeconds = Math.round(asset.duration);

      const { data: vodData, error: insertError } = await supabase.from('vod_archive').insert({
        title: vodTitle,
        mux_stream_id: stream.id,
        mux_asset_id: assetId,
        playback_id: playbackId,
        duration_seconds: durationSeconds,
        resolution: '1440p',
      }).select().single();

      if (insertError) throw insertError;
      
      setError(null);
      setStreamVODs(prev => ({
        ...prev,
        [stream.id]: {
          id: vodData.id,
          title: vodTitle,
          mux_asset_id: assetId,
          playback_id: playbackId,
          duration_seconds: durationSeconds,
          created_at: vodData.created_at
        }
      }));
      alert('VOD archived successfully!');
      fetchArchivedVODs();
    } catch (err) {
      console.error('Error archiving stream:', err);
      setError(err.message);
    } finally {
      setCreatingStream(false);
    }
  };

  const fetchArchivedVODs = async () => {
    try {
      setLoadingVODs(true);

      const { data: vods, error: vodsError } = await supabase
        .from('vod_archive')
        .select('id, title, duration_seconds, mux_asset_id, created_at')
        .order('created_at', { ascending: false });

      if (vodsError) throw vodsError;
      setArchivedVODs(vods || []);
    } catch (err) {
      console.error('Error fetching archived VODs:', err);
    } finally {
      setLoadingVODs(false);
    }
  };

  const unarchiveVOD = async (vodId) => {
    if (window.confirm('Are you sure you want to delete this archived VOD?')) {
      try {
        const { error: deleteError } = await supabase
          .from('vod_archive')
          .delete()
          .eq('id', vodId);

        if (deleteError) throw deleteError;
        
        alert('VOD unarchived successfully!');
        fetchArchivedVODs();
      } catch (err) {
        console.error('Error unarchiving VOD:', err);
        setError(err.message);
      }
    }
  };

  const deleteStream = async (streamId) => {
    if (window.confirm('Are you sure you want to delete this stream?')) {
      try {
        await muxService.deleteLiveStream(streamId);

        const { error: dbError } = await supabase
          .from('streams_metadata')
          .delete()
          .eq('mux_stream_id', streamId)
          .eq('user_id', user.id);

        if (dbError) throw dbError;

        setStreams(streams.filter((s) => s.id !== streamId));
      } catch (err) {
        console.error('Error deleting stream:', err);
        setError(err.message);
      }
    }
  };

  return (
    <div className="streams-manager">
      <h2>Live Streams Manager</h2>

      {error && <div className="error-message">{error}</div>}

      <div className="create-stream-section">
        <h3>Create New Stream</h3>
        <div className="create-stream-form">
          <input
            type="text"
            placeholder="Stream name (e.g., Week 5 Game)"
            value={createStreamName}
            onChange={(e) => setCreateStreamName(e.target.value)}
            disabled={creatingStream || loadingGames}
          />
          <button onClick={initiateStreamCreation} disabled={creatingStream || loadingGames} className="btn-primary">
            {creatingStream || loadingGames ? 'Loading...' : 'Create Stream'}
          </button>
        </div>
      </div>

      <div className="streams-list">
        <h3>Active Streams</h3>

        {loading ? (
          <p>Loading streams...</p>
        ) : streams.length === 0 ? (
          <p>No streams found</p>
        ) : (
          <div className="streams-grid">
            {streams.map((stream) => (
              <div
                key={stream.id}
                className={`stream-card ${selectedStream?.id === stream.id ? 'selected' : ''}`}
                onClick={() => {
                  if (selectedStream?.id === stream.id) {
                    setSelectedStream(null);
                  } else {
                    setSelectedStream(stream);
                    fetchStreamVOD(stream.id);
                  }
                }}
              >
                <div className="stream-header">
                  <h4>{stream.metadata?.display_title || stream.metadata?.stream_name || stream.id}</h4>
                  <span className={`status ${stream.status}`}>{stream.status}</span>
                </div>

                {selectedStream?.id === stream.id && (
                  <div className="stream-details">
                    <div className="detail-row" onClick={(e) => e.stopPropagation()}>
                      <label>Ingest URL:</label>
                      <div className="copy-container">
                        <code>{muxService.getIngestUrl(stream)}</code>
                        <button
                          onClick={(e) => copyToClipboard(muxService.getIngestUrl(stream), e)}
                          className="copy-btn"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="detail-row" onClick={(e) => e.stopPropagation()}>
                      <label>Stream Key:</label>
                      <div className="copy-container">
                        <code>{muxService.getStreamKey(stream)}</code>
                        <button
                          onClick={(e) => copyToClipboard(muxService.getStreamKey(stream), e)}
                          className="copy-btn"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="detail-row" onClick={(e) => e.stopPropagation()}>
                      <label>Playback ID:</label>
                      <div className="copy-container">
                        <code>{muxService.getPlaybackId(stream)}</code>
                        <button
                          onClick={(e) => copyToClipboard(muxService.getPlaybackId(stream), e)}
                          className="copy-btn"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="detail-row" onClick={(e) => e.stopPropagation()}>
                      <label>OBS Chat Widget URL:</label>
                      <div className="copy-container">
                        <code>{`${window.location.origin}/chat-widget/${stream.id}`}</code>
                        <button
                          onClick={(e) => copyToClipboard(`${window.location.origin}/chat-widget/${stream.id}`, e)}
                          className="copy-btn"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    {streamVODs[stream.id] && (
                      <div className="detail-row vod-link-section" onClick={(e) => e.stopPropagation()}>
                        <label>📹 VOD Playback Link:</label>
                        <div className="copy-container">
                          <code>{muxService.getVODPlaybackUrl(streamVODs[stream.id].playback_id)}</code>
                          <button
                            onClick={(e) => copyToClipboard(muxService.getVODPlaybackUrl(streamVODs[stream.id].playback_id), e)}
                            className="copy-btn"
                          >
                            Copy
                          </button>
                        </div>
                        <p className="vod-info">Duration: {Math.floor(streamVODs[stream.id].duration_seconds / 60)} min</p>
                      </div>
                    )}

                    <div className="stream-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveStreamVOD(stream);
                        }}
                        className="btn-secondary"
                        title="Archive the VOD to the archive"
                        disabled={creatingStream}
                      >
                        {creatingStream ? 'Archiving...' : 'Archive VOD'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          disableStream(stream.id);
                        }}
                        className="btn-secondary"
                        title="Disable this stream"
                      >
                        Disable Stream
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteStream(stream.id);
                        }}
                        className="btn-danger"
                        title="Delete this stream"
                      >
                        Delete
                      </button>
                    </div>

                    <div className="stream-chat-section" onClick={(e) => e.stopPropagation()}>
                      <LiveChat 
                        streamId={stream.id} 
                        username={user?.email || 'Admin'}
                        isAdmin={true}
                        userId={user?.id}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateGameModal && (
        <div className="modal-overlay" onClick={() => setShowCreateGameModal(false)}>
          <div className="modal-content modal-with-standings" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select the Game for This Week</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowCreateGameModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="games-section">
                <h4>Games This Week</h4>
                <div className="games-list">
                  {weekGames.length === 0 ? (
                    <p>No games found for this week</p>
                  ) : (
                    weekGames.map((game) => {
                      const homeStanding = teamStandings.find(s => s.team?.id === game.home_team_id);
                      const awayStanding = teamStandings.find(s => s.team?.id === game.away_team_id);
                      
                      return (
                        <div key={game.id} className="game-selection">
                          <div className="game-matchup">
                            <div className="team-with-rank">
                              {game.home_team?.logo_url && (
                                <img src={game.home_team.logo_url} alt={game.home_team.name} />
                              )}
                              <div className="team-info">
                                <span className="rank">#{homeStanding?.rank || '-'}</span>
                                <span className="team-name">{game.home_team?.name}</span>
                              </div>
                            </div>
                            <span className="vs">@</span>
                            <div className="team-with-rank">
                              <div className="team-info">
                                <span className="rank">#{awayStanding?.rank || '-'}</span>
                                <span className="team-name">{game.away_team?.name}</span>
                              </div>
                              {game.away_team?.logo_url && (
                                <img src={game.away_team.logo_url} alt={game.away_team.name} />
                              )}
                            </div>
                          </div>
                          <button
                            className="btn-primary"
                            onClick={() => createNewStream(game)}
                            disabled={creatingStream}
                          >
                            {creatingStream ? 'Creating...' : 'Select'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="standings-section">
                <h4>Team Standings</h4>
                <div className="standings-table">
                  {teamStandings.length === 0 ? (
                    <p>No standings available</p>
                  ) : (
                    <>
                      <div className="standings-header">
                        <div className="standings-rank">Rank</div>
                        <div className="standings-team">Team</div>
                        <div className="standings-record">W-L</div>
                        <div className="standings-pf">PF</div>
                        <div className="standings-pa">PA</div>
                      </div>
                      {teamStandings.map((standing) => (
                        <div key={standing.id} className="standings-row">
                          <div className="standings-rank">{standing.rank}</div>
                          <div className="standings-team">
                            {standing.team?.logo_url && (
                              <img src={standing.team.logo_url} alt={standing.team.name} className="standings-logo" />
                            )}
                            <span>{standing.team?.name}</span>
                          </div>
                          <div className="standings-record">{standing.wins}-{standing.losses}</div>
                          <div className="standings-pf">{standing.points_for}</div>
                          <div className="standings-pa">{standing.points_against}</div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="archived-vods-section">
        <h3>Archived VODs</h3>
        
        {loadingVODs ? (
          <p>Loading archived VODs...</p>
        ) : archivedVODs.length === 0 ? (
          <p>No archived VODs yet</p>
        ) : (
          <div className="vods-list">
            {archivedVODs.map((vod) => (
              <div key={vod.id} className="vod-card">
                <div className="vod-info">
                  <h4>{vod.title}</h4>
                  <p className="vod-meta">
                    {Math.round(vod.duration_seconds / 60)} min • {new Date(vod.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => unarchiveVOD(vod.id)}
                  className="btn-danger"
                  title="Delete this archived VOD"
                >
                  Unarchive
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="streaming-instructions">
        <h3>How to Stream via OBS</h3>
        <ol>
          <li>Create a new stream above</li>
          <li>Click on the stream to expand it and see the Ingest URL and Stream Key</li>
          <li>Open OBS and go to Settings → Stream</li>
          <li>Set Service to Custom RTMP Server</li>
          <li>Paste the Ingest URL in the Server field</li>
          <li>Paste the Stream Key in the Stream Key field</li>
          <li>Click Start Streaming to begin broadcasting</li>
        </ol>
      </div>
    </div>
  );
};

export default StreamsManager;
